import { message } from 'antd';
import TokenManager from './TokenManager';
import NotFoundError from '../errors/NotFoundError';
import Config from './Config';
import RequestTrackerService from './RequestTrackerService';
import * as Sentry from '@sentry/react';

class HttpRequest {
  private async runInTokenSafeMode(
    path: string,
    params: any,
    headers: any,
    signal?: AbortSignal // Add signal here
  ) {
    const token = await TokenManager.getToken();

    let result = await fetch(path, {
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`
      },
      ...params,
      signal // Pass signal to fetch
    });

    // if the status code is 401, we need to fetch a new token and retry the request
    if (result.status === 401) {
      await TokenManager.fetchToken();
      const newToken = await TokenManager.getToken();

      result = await fetch(path, {
        headers: {
          ...headers,
          Authorization: `Bearer ${newToken}`
        },
        ...params,
        signal // Pass signal to fetch again
      });
    }

    return result;
  }

  async makeGetJSONRequest(path: string, params, signal?: AbortSignal) {
    let _params = new URLSearchParams(params);

    let fullApiPath = new URL(path, Config.getConfig().backend.url);
    fullApiPath.search = _params.toString();

    try {
      RequestTrackerService.startRequest();

      let result = await this.runInTokenSafeMode(
        fullApiPath.toString(),
        {
          method: 'GET'
        },
        {
          'Content-Type': 'application/json'
        },
        signal // Pass signal to runInTokenSafeMode
      );

      if (!result.ok) {
        if (result.status === 404) {
          throw new NotFoundError('Resource not found');
        }

        throw new Error(`HTTP error! status: ${result.status}`);
      }

      let data = await result.json();
      return data;

      // Save the token
    } catch (error: any) {
      Sentry.withScope((scope) => {
        scope.setExtra('requestPayload', params);
        scope.setExtra('responseData', error.response?.data);
        scope.setExtra('httpStatusCode', error.response?.status);
        Sentry.captureException(error);
      });

      console.error('Error while making request: ' + error.message);
      throw error;
    } finally {
      RequestTrackerService.endRequest();
    }
  }

  async makePostJSONRequest(path: string, body, signal?: AbortSignal) {
    let fullApiPath = new URL(path, Config.getConfig().backend.url);

    try {
      RequestTrackerService.startRequest();

      let result = await this.runInTokenSafeMode(
        fullApiPath.toString(),
        {
          method: 'POST',
          body: JSON.stringify(body)
        },
        {
          'Content-Type': 'application/json'
        },
        signal // Pass signal to runInTokenSafeMode
      );

      // The result.ok property in the Fetch API will be false when the HTTP status code of the response is not in the successful range (200-299).
      if (!result.ok) {
        throw new Error(`HTTP error! status: ${result.status}`);
      }

      let data = await result.json();
      return data;

      // Save the token
    } catch (error: any) {
      Sentry.withScope((scope) => {
        scope.setExtra('requestPayload', body);
        scope.setExtra('responseData', error.response?.data);
        scope.setExtra('httpStatusCode', error.response?.status);
        Sentry.captureException(error);
      });

      message.error('Error while making request: ' + error.message);
      throw error;
    } finally {
      RequestTrackerService.endRequest();
    }
  }

  async makePostFormDataRequest(
    path: string,
    formData: FormData,
    handleResponse?: (response: any) => void,
    signal?: AbortSignal // Add signal here
  ): Promise<{ data: any }> {
    const token = await TokenManager.getToken();

    let fullApiPath = new URL(path, Config.getConfig().backend.url);

    try {
      RequestTrackerService.startRequest();

      let result = await this.runInTokenSafeMode(
        fullApiPath.toString(),
        {
          method: 'POST',
          body: formData
        },
        {},
        signal // Pass signal to runInTokenSafeMode
      );

      if (handleResponse) {
        handleResponse(result);
      } else {
        // The result.ok property in the Fetch API will be false when the HTTP status code of the response is not in the successful range (200-299).
        if (!result.ok) {
          throw new Error(`HTTP error! status: ${result.status}`);
        }
      }

      let data = await result.json();
      return { data: data };

      // Save the token
    } catch (error: any) {
      Sentry.withScope((scope) => {
        scope.setExtra('requestPayload', formData);
        scope.setExtra('responseData', error.response?.data);
        scope.setExtra('httpStatusCode', error.response?.status);
        Sentry.captureException(error);
      });

      message.error('Error while making request: ' + error.message);
      throw error;
    } finally {
      RequestTrackerService.endRequest();
    }
  }
}

export default new HttpRequest();
